import { useId, useState, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useActiveCompany } from '@/lib/active-company-context';
import { purposeLabel } from '@/features/watermark/presentation';
import { PURPOSES, type Purpose } from '@/features/watermark/types';
import { useWatermarkSettings } from '@/features/watermark/use-watermark';
import { useWatermarkedCopy } from '@/features/watermark/use-watermarked-copy';
import { skeletonLine } from '@/components/ui/skeleton';

export type CopyMode = 'print_view' | 'download';

interface Props {
  mode: CopyMode | null;
  entryId: string;
  code: string;
  onClose: () => void;
}

const TEXT: Record<CopyMode, { title: string; confirm: string; busy: string }> = {
  print_view: {
    title: 'Vista con marca de agua',
    confirm: 'Abrir para imprimir',
    busy: 'Generando…',
  },
  download: { title: 'Descargar PDF', confirm: 'Descargar', busy: 'Generando…' },
};

/** Elección del tipo de copia (solo los activos en la configuración de la empresa) antes
 *  de generar la copia controlada. La copia registra su evento en la bitácora. */
export function PrintCopyModal({ mode, ...rest }: Props): ReactElement | null {
  // Montado solo abierto: la elección y el error parten limpios en cada apertura.
  return mode === null ? null : <Dialog mode={mode} {...rest} />;
}

function Dialog({
  mode,
  entryId,
  code,
  onClose,
}: Omit<Props, 'mode'> & { mode: CopyMode }): ReactElement {
  const titleId = useId();
  const { activeCompanyId } = useActiveCompany();
  const settings = useWatermarkSettings({
    companyId: activeCompanyId ?? '',
    enabled: activeCompanyId !== null,
  });
  const copy = useWatermarkedCopy();
  const [picked, setPicked] = useState<Purpose | null>(null);

  const legends = settings.data?.legends ?? [];
  const active = PURPOSES.filter((p) => legends.some((l) => l.key === p && l.enabled));
  const purpose = picked !== null && active.includes(picked) ? picked : (active[0] ?? null);
  const text = TEXT[mode];

  return (
    <ModalShell
      open
      title={text.title}
      busy={copy.pending}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={copy.pending}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm"
            disabled={purpose === null || copy.pending}
            onClick={() => purpose && copy.run({ entryId, code, purpose, mode }, onClose)}
          >
            {copy.pending ? text.busy : text.confirm}
          </button>
        </>
      }
    >
      <div className="modal-body">
        {settings.isLoading ? (
          <div aria-busy="true" aria-label="Cargando tipos de copia">
            <div className={skeletonLine({ width: 70, space: 'y' })} />
            <div className={skeletonLine({ width: 50, space: 'y' })} />
          </div>
        ) : settings.isError ? (
          <p className="fld-err" role="status">
            No se pudieron cargar los tipos de copia. Intenta de nuevo.
          </p>
        ) : active.length === 0 ? (
          <p className="soft" role="status">
            No hay tipos de copia activos. Pide a Calidad que active una leyenda en Marca de agua e
            impresión.
          </p>
        ) : (
          <fieldset className="fld">
            <legend className="fld-lab">Tipo de copia</legend>
            {active.map((p) => (
              <label key={p} className="chk">
                <input
                  type="radio"
                  name={`${titleId}-purpose`}
                  value={p}
                  checked={purpose === p}
                  disabled={copy.pending}
                  onChange={() => setPicked(p)}
                />
                <span>
                  {purposeLabel(p)}{' '}
                  <span className="code">{legends.find((l) => l.key === p)?.text}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        <p className="soft" style={{ fontSize: 11.5, marginTop: 8 }}>
          La copia lleva la marca de agua y un folio único, y queda en el registro de copias.
        </p>
        {copy.error && (
          <p className="fld-err" role="alert" style={{ marginTop: 8 }}>
            {copy.error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
